import { useEffect, useState } from "react";
import { Card, Col, Row, Spinner } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faUserTie, faChevronDown, faChevronUp,
    faCheckCircle, faTimesCircle, faTriangleExclamation,
    faFingerprint, faFaceSmile,
} from "@fortawesome/free-solid-svg-icons";
import { ClientService } from "../../component/services/client.service";
import { makeParams } from "../../component/api/makeRequest";
import { DateRange } from "../../component/DateRangeSelector/DateRangeSelector";
import { useTheme } from "../../theme/useTheme";
import moment from "moment";

interface Operator {
    operatorId: string;
    operatorName: string;
    emailId: string;
    collegeName: string;
    collegId: string;
    totalStudents: number;
    distinctSuccess: number;
    distinctFailure: number;
    facialFailed: number;
    fingerprintFailed: number;
}

interface VendorOperatorStat {
    vendorName: string;
    totalStudents: number;
    totalFailures: number;
    operators: Operator[];
}

interface Props { dateRange: DateRange; }

function FailureRateBar({ success, failure }: { success: number; failure: number }) {
    const total = success + failure;
    const failPct = total ? (failure / total) * 100 : 0;
    const color = failPct >= 50 ? "#ef4444" : failPct >= 25 ? "#f59e0b" : "#10b981";
    return (
        <div style={{ display: "flex", alignItems: "center", gap: 6, minWidth: 120 }}>
            <div style={{ flex: 1, height: 6, borderRadius: 99, background: "#f3f4f6", overflow: "hidden" }}>
                <div style={{ width: `${failPct}%`, height: "100%", background: color, borderRadius: 99, transition: "width 0.4s" }} />
            </div>
            <span style={{ fontSize: "0.72rem", fontWeight: 600, color, minWidth: 32 }}>
                {Math.round(failPct)}%
            </span>
        </div>
    );
}

function VendorOperatorCard({ vendor }: { vendor: VendorOperatorStat }) {
    const { colors, colorSchemes } = useTheme();
    const [expanded, setExpanded] = useState(true);
    const [showAll, setShowAll] = useState(false);

    const displayed = showAll ? vendor.operators : vendor.operators.slice(0, 5);
    const rest = vendor.operators.length - 5;

    const thStyle: React.CSSProperties = {
        padding: "8px 12px",
        fontSize: "0.68rem",
        fontWeight: 600,
        color: colors.text.muted,
        textTransform: "uppercase",
        letterSpacing: "0.05em",
        borderBottom: `1px solid ${colors.border.light}`,
        whiteSpace: "nowrap",
        background: colors.background.secondary,
    };

    const tdStyle: React.CSSProperties = {
        padding: "10px 12px",
        borderBottom: `1px solid ${colors.border.light}`,
        fontSize: "0.82rem",
        verticalAlign: "middle",
    };

    const overallFailPct = vendor.totalStudents
        ? Math.round((vendor.totalFailures / vendor.totalStudents) * 100)
        : 0;
    const headerColor = overallFailPct >= 50 ? "#ef4444" : overallFailPct >= 25 ? "#f59e0b" : "#10b981";

    return (
        <Card style={{ ...colorSchemes.card, borderRadius: "14px", border: `1px solid ${colors.border.light}`, overflow: "hidden" }}>
            {/* Vendor header */}
            <div
                className="d-flex align-items-center justify-content-between px-4 py-3"
                style={{ background: colors.background.secondary, borderBottom: expanded ? `1px solid ${colors.border.light}` : "none", cursor: "pointer", userSelect: "none" }}
                onClick={() => setExpanded(v => !v)}
            >
                <div className="d-flex align-items-center gap-3">
                    <div style={{ width: 36, height: 36, borderRadius: "9px", background: `${headerColor}18`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                        <FontAwesomeIcon icon={faUserTie} style={{ color: headerColor, fontSize: "0.9rem" }} />
                    </div>
                    <div>
                        <div style={{ fontWeight: 700, fontSize: "0.95rem", color: colors.text.primary }}>{vendor.vendorName}</div>
                        <div style={{ fontSize: "0.72rem", color: colors.text.muted }}>
                            {vendor.operators.length} operators &middot; {vendor.totalFailures.toLocaleString()} failures &middot;
                            <span style={{ color: headerColor, fontWeight: 600 }}> {overallFailPct}% fail rate</span>
                        </div>
                    </div>
                </div>
                <FontAwesomeIcon icon={expanded ? faChevronUp : faChevronDown} style={{ color: colors.text.muted, fontSize: "0.8rem" }} />
            </div>

            {expanded && (
                <div style={{ overflowX: "auto" }}>
                    <table style={{ width: "100%", borderCollapse: "collapse" }}>
                        <thead>
                            <tr>
                                <th style={thStyle}>#</th>
                                <th style={thStyle}>Operator</th>
                                <th style={thStyle}>College</th>
                                <th style={thStyle}>Students</th>
                                <th style={thStyle}>
                                    <span className="d-flex align-items-center gap-1">
                                        <FontAwesomeIcon icon={faCheckCircle} style={{ color: "#10b981" }} /> Success
                                    </span>
                                </th>
                                <th style={thStyle}>
                                    <span className="d-flex align-items-center gap-1">
                                        <FontAwesomeIcon icon={faTimesCircle} style={{ color: "#ef4444" }} /> Failure
                                    </span>
                                </th>
                                <th style={thStyle}>Fail Rate</th>
                                <th style={thStyle}>
                                    <span className="d-flex align-items-center gap-1">
                                        <FontAwesomeIcon icon={faFaceSmile} style={{ color: "#3b82f6" }} /> Facial
                                    </span>
                                </th>
                                <th style={thStyle}>
                                    <span className="d-flex align-items-center gap-1">
                                        <FontAwesomeIcon icon={faFingerprint} style={{ color: "#f59e0b" }} /> Fingerprint
                                    </span>
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {displayed.map((op, i) => {
                                const failPct = op.totalStudents ? (op.distinctFailure / op.totalStudents) * 100 : 0;
                                const rowWarning = failPct >= 50;
                                return (
                                    <tr
                                        key={op.operatorId ?? i}
                                        style={{ background: rowWarning ? "#fff5f5" : i % 2 === 0 ? colors.background.primary : colors.background.secondary, transition: "background 0.15s" }}
                                        onMouseEnter={(e) => (e.currentTarget.style.background = colors.background.tertiary)}
                                        onMouseLeave={(e) => (e.currentTarget.style.background = rowWarning ? "#fff5f5" : i % 2 === 0 ? colors.background.primary : colors.background.secondary)}
                                    >
                                        <td style={{ ...tdStyle, color: colors.text.muted, width: 36 }}>{i + 1}</td>

                                        {/* Operator name + email */}
                                        <td style={{ ...tdStyle, fontWeight: 600, color: colors.text.primary }}>
                                            <div className="d-flex align-items-center gap-2">
                                                {rowWarning && (
                                                    <FontAwesomeIcon icon={faTriangleExclamation} style={{ color: "#ef4444", fontSize: "0.75rem" }} />
                                                )}
                                                <div>
                                                    <div>{op.operatorName || "—"}</div>
                                                    {op.emailId && (
                                                        <div style={{ fontSize: "0.7rem", color: colors.text.muted, fontWeight: 400 }}>{op.emailId}</div>
                                                    )}
                                                </div>
                                            </div>
                                        </td>

                                        {/* College */}
                                        <td style={{ ...tdStyle, color: colors.text.secondary }}>
                                            {op.collegeName || "—"}
                                        </td>

                                        {/* Total students */}
                                        <td style={{ ...tdStyle, color: colors.text.secondary }}>
                                            {op.totalStudents.toLocaleString()}
                                        </td>

                                        {/* Success */}
                                        <td style={{ ...tdStyle, color: "#10b981", fontWeight: 600 }}>
                                            {op.distinctSuccess.toLocaleString()}
                                        </td>

                                        {/* Failure */}
                                        <td style={{ ...tdStyle, color: "#ef4444", fontWeight: 600 }}>
                                            {op.distinctFailure.toLocaleString()}
                                        </td>

                                        {/* Fail rate bar */}
                                        <td style={tdStyle}>
                                            <FailureRateBar success={op.distinctSuccess} failure={op.distinctFailure} />
                                        </td>

                                        {/* Facial failed */}
                                        <td style={{ ...tdStyle, color: "#3b82f6", fontWeight: 500 }}>
                                            {op.facialFailed.toLocaleString()}
                                        </td>

                                        {/* Fingerprint failed */}
                                        <td style={{ ...tdStyle, color: "#f59e0b", fontWeight: 500 }}>
                                            {op.fingerprintFailed.toLocaleString()}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>

                    {rest > 0 && (
                        <div className="px-4 py-2">
                            <button
                                className="btn btn-link btn-sm p-0"
                                style={{ color: colors.primary.main, fontSize: "0.78rem" }}
                                onClick={() => setShowAll(v => !v)}
                            >
                                {showAll ? "Show less" : `+${rest} more operators`}
                            </button>
                        </div>
                    )}
                </div>
            )}
        </Card>
    );
}

export default function VendorOperatorDrilldown({ dateRange }: Props) {
    const { colors } = useTheme();
    const [data, setData] = useState<VendorOperatorStat[]>([]);
    const [loading, setLoading] = useState(false);

    async function fetchData(start: Date, end: Date) {
        setLoading(true);
        setData([]);
        const params = makeParams([
            { index: "start_time", value: moment(start).toISOString() },
            { index: "end_time", value: moment(end).toISOString() },
        ]);
        await ClientService.getVendorOperatorStats(params)
            .then((res: any) => setData(res.data?.data || []))
            .catch((err: any) => console.log(err))
            .finally(() => setLoading(false));
    }

    useEffect(() => {
        fetchData(dateRange.startDate, dateRange.endDate);
    }, [dateRange.startDate, dateRange.endDate]);

    return (
        <div className="mt-4 mb-4">
            <div className="mb-3">
                <h6 style={{ fontWeight: 700, color: colors.text.primary, fontSize: "1rem", marginBottom: 2 }}>
                    Operator-level Drill-down
                </h6>
                <span style={{ color: colors.text.muted, fontSize: "0.78rem" }}>
                    Operators ranked by failure count within each vendor &mdash; <span style={{ color: "#ef4444" }}>red rows</span> have &ge;50% fail rate
                </span>
            </div>

            {loading ? (
                <div className="d-flex align-items-center justify-content-center py-5">
                    <Spinner animation="border" style={{ color: colors.primary.main }} />
                </div>
            ) : data.length === 0 ? (
                <div className="d-flex flex-column align-items-center justify-content-center py-5">
                    <FontAwesomeIcon icon={faUserTie} style={{ fontSize: "2.5rem", color: colors.border.secondary, marginBottom: "1rem" }} />
                    <span style={{ color: colors.text.muted, fontSize: "0.9rem" }}>No operator data for the selected range</span>
                </div>
            ) : (
                <Row className="g-3">
                    {data.map(vendor => (
                        <Col key={vendor.vendorName} xs={12}>
                            <VendorOperatorCard vendor={vendor} />
                        </Col>
                    ))}
                </Row>
            )}
        </div>
    );
}
